import React, { useMemo, useState } from 'react';

import { link } from '@aline/routes';
import type { TypographyProps } from '@mui/material';
import { alpha, Box, IconButton, Popover, Typography } from '@mui/material';
import clsx from 'clsx';
import { Pencil, PlusCircleIcon } from 'lucide-react';
import { useNavigate } from 'react-router-dom';
import { AlineCheckbox } from 'src/components/generic/AlineCheckbox';
import { AlineTooltip } from 'src/components/generic/AlineTooltip';
import { LucideIconRenderer } from 'src/components/generic/LucideIconRenderer';
import { SearchInput } from 'src/components/SearchInput';
import { CreateBoardDialog } from 'src/components/workflow/board/CreateBoardDialog';
import type { Board } from 'src/gql/generated/apollo-types';
import { makeStyles } from 'tss-react/mui';

const useStyles = makeStyles<void, 'pencilButton'>()((theme, _params, classes) => ({
  addNewItem: {
    color: theme.palette.primary.main,
  },
  boardsList: {
    maxHeight: 400,
    overflowY: 'auto',
  },
  emptyBoardsList: {
    color: theme.palette.text.secondary,
    padding: theme.spacing(2, 2, 1, 2),
  },
  icon: {
    alignItems: 'center',
    borderRadius: 4,
    display: 'flex',
    fontSize: 20,
    height: 24,
    justifyContent: 'center',
    width: 24,
  },
  menuItem: {
    '&:hover': {
      backgroundColor: theme.palette.action.hover,
    },
    [`&:hover .${classes.pencilButton}`]: {
      opacity: 1,
      visibility: 'visible',
    },
    alignItems: 'center',
    cursor: 'pointer',
    display: 'flex',
    gap: theme.spacing(1),
    padding: theme.spacing(0.5, 2),
    transition: 'background-color 0.2s',
  },
  menuItemSelected: {
    backgroundColor: theme.palette.action.selected,
  },
  name: {
    flex: 1,
    minWidth: 0,
  },
  pencilButton: {
    color: theme.palette.text.secondary,
    marginLeft: 'auto',
    opacity: 0,
    padding: theme.spacing(0.5),
    transition: 'opacity 0.2s ease-in-out',
    visibility: 'hidden',
  },
  popoverPaper: {
    borderRadius: 8,
    display: 'flex',
    flexDirection: 'column',
    gap: theme.spacing(1),
    minWidth: 300,
    padding: theme.spacing(2, 0),
  },
  searchContainer: {
    padding: theme.spacing(0, 2),
  },
  triggerAnchor: {
    display: 'inline-flex',
  },
}));

export type BoardOption = Pick<Board, 'color' | 'icon' | 'id' | 'name'>;

export interface BoardFilterPickerProps {
  boards: BoardOption[];
  mode: 'single' | 'multi';
  onChange: (boardIds: string[]) => void;
  renderTrigger: (
    currentBoard: BoardOption | undefined,
    onClick?: (e: React.MouseEvent<HTMLElement>) => void,
    isOpen?: boolean
  ) => React.ReactNode;
  selectedBoardIds: string[];
  /**
   * Editing a board navigates to board settings in the same tab. Callers that
   * render the picker inside a form must turn this off so the pencil cannot
   * discard unsaved input.
   */
  showEditAction?: boolean;
}

export function BoardFilterPicker({
  boards,
  mode,
  onChange,
  renderTrigger,
  selectedBoardIds,
  showEditAction = true,
}: BoardFilterPickerProps): JSX.Element {
  const { classes } = useStyles();
  const navigate = useNavigate();
  const [open, setOpen] = useState(false);
  const [searchTerm, setSearchTerm] = useState('');
  const [anchorEl, setAnchorEl] = useState<HTMLElement | null>(null);
  const [isCreateDialogOpen, setIsCreateDialogOpen] = useState(false);
  const selectedIdSet = useMemo(() => new Set(selectedBoardIds), [selectedBoardIds]);
  const currentBoard = boards.find(board => selectedIdSet.has(board.id));

  const filteredBoards = useMemo(() => {
    if (!searchTerm.trim()) return boards;
    return boards.filter(board => board.name.toLowerCase().includes(searchTerm.toLowerCase()));
  }, [boards, searchTerm]);

  const closePopover = () => {
    setOpen(false);
    setSearchTerm('');
  };

  const openCreateDialog = () => {
    setIsCreateDialogOpen(true);
    closePopover();
  };

  const handleEditBoard = (boardId: string) => {
    closePopover();
    navigate(link.WorkflowBoardSettings({ boardId }));
  };

  const handleBoardToggle = (boardId: string) => {
    if (mode === 'single') {
      onChange([boardId]);
      closePopover();
      return;
    }

    if (selectedIdSet.has(boardId)) {
      onChange(selectedBoardIds.filter(id => id !== boardId));
      return;
    }
    onChange([...selectedBoardIds, boardId]);
  };

  return (
    <>
      <div
        ref={setAnchorEl}
        className={classes.triggerAnchor}
        onClick={e => {
          e.stopPropagation();
          setOpen(true);
        }}
      >
        {renderTrigger(
          currentBoard,
          e => {
            e.stopPropagation();
            setOpen(true);
          },
          open
        )}
      </div>
      <Popover
        anchorEl={anchorEl}
        anchorOrigin={{
          horizontal: 'left',
          vertical: 'bottom',
        }}
        open={open}
        slotProps={{ paper: { className: classes.popoverPaper } }}
        transformOrigin={{
          horizontal: 'left',
          vertical: 'top',
        }}
        onClose={closePopover}
      >
        <div className={classes.searchContainer}>
          <SearchInput
            minWidth="100%"
            placeholder="Search for a board..."
            search={searchTerm}
            setSearch={setSearchTerm}
          />
        </div>
        <div className={classes.boardsList}>
          <BoardFilterPickerItems
            boards={filteredBoards}
            selectedIdSet={selectedIdSet}
            showEditAction={showEditAction}
            onBoardClick={handleBoardToggle}
            onEditBoard={handleEditBoard}
          />
        </div>
        <BoardFilterPickerMenuItem
          className={classes.addNewItem}
          icon={<PlusCircleIcon size={20} />}
          text="Add new"
          typographyProps={{ color: 'primary', variant: 'subtitle2' }}
          onClick={openCreateDialog}
        />
      </Popover>
      <CreateBoardDialog open={isCreateDialogOpen} onClose={() => setIsCreateDialogOpen(false)} />
    </>
  );
}

interface BoardFilterPickerItemsProps {
  boards: BoardOption[];
  onBoardClick: (boardId: string) => void;
  onEditBoard: (boardId: string) => void;
  selectedIdSet: Set<string>;
  showEditAction: boolean;
}

function BoardFilterPickerItems({
  boards,
  onBoardClick,
  onEditBoard,
  selectedIdSet,
  showEditAction,
}: BoardFilterPickerItemsProps): JSX.Element {
  const { classes } = useStyles();

  if (!boards.length) {
    return (
      <div className={classes.emptyBoardsList}>
        <Typography align="center" variant="body2">
          No boards found
        </Typography>
      </div>
    );
  }

  return (
    <>
      {boards.map(board => {
        const isSelected = selectedIdSet.has(board.id);
        return (
          <div
            key={board.id}
            className={clsx(classes.menuItem, isSelected && classes.menuItemSelected)}
            onClick={() => onBoardClick(board.id)}
          >
            <AlineCheckbox
              checked={isSelected}
              size="small"
              onChange={() => onBoardClick(board.id)}
              onClick={e => e.stopPropagation()}
            />
            <div className={classes.icon} style={{ backgroundColor: alpha(board.color, 0.1) }}>
              <LucideIconRenderer color={board.color} icon={board.icon} size={16} />
            </div>
            <Typography noWrap className={classes.name}>
              {board.name || 'Untitled Board'}
            </Typography>
            {showEditAction && (
              <AlineTooltip arrow placement="top" title={`Edit "${board.name}" board`}>
                <IconButton
                  aria-label={`Edit "${board.name}" board`}
                  className={classes.pencilButton}
                  size="small"
                  onClick={e => {
                    e.stopPropagation();
                    onEditBoard(board.id);
                  }}
                >
                  <Pencil size={16} />
                </IconButton>
              </AlineTooltip>
            )}
          </div>
        );
      })}
    </>
  );
}

interface BoardFilterPickerMenuItemProps {
  icon: React.ReactNode;
  onClick: () => void;
  text: string;
  className?: string;
  typographyProps?: TypographyProps;
}

function BoardFilterPickerMenuItem({
  className,
  icon,
  onClick,
  text,
  typographyProps,
}: BoardFilterPickerMenuItemProps): JSX.Element {
  const { classes } = useStyles();

  return (
    <div className={clsx(classes.menuItem, className)} onClick={onClick}>
      <Box className={classes.icon}>{icon}</Box>
      <Typography {...typographyProps}>{text}</Typography>
    </div>
  );
}

/** Backward-compatible single-select board picker used outside the workflows toolbar. */
interface BoardListProps {
  boards: BoardOption[];
  renderTrigger: BoardFilterPickerProps['renderTrigger'];
  onBoardChange?: (boardId: string) => void;
  selectedBoardId?: string;
  showEditAction?: boolean;
}

export function BoardList({
  boards,
  onBoardChange,
  renderTrigger,
  selectedBoardId,
  showEditAction,
}: BoardListProps): JSX.Element {
  return (
    <BoardFilterPicker
      boards={boards}
      mode="single"
      renderTrigger={renderTrigger}
      selectedBoardIds={selectedBoardId ? [selectedBoardId] : []}
      showEditAction={showEditAction}
      onChange={boardIds => {
        const boardId = boardIds.at(0);
        if (!boardId) return;

        onBoardChange?.(boardId);
      }}
    />
  );
}
